import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from "react";

import { api } from "../api.js";
import { EventTimestampBlock } from "../components/EventTimestampBlock.js";
import {
  segmentKey,
  segmentRawEventsFilters,
  SessionJourneyBar,
} from "../components/SessionJourneyBar.js";
import {
  CLIENT_ERROR_NOTES_STORAGE_KEY,
  CLIENT_ERROR_REVIEW_STORAGE_KEY,
  clientErrorReviewIcon,
  type ClientErrorReviewStatus,
  cycleClientErrorReviewStatus,
  getClientErrorNote,
  getClientErrorReviewStatus,
  reviewStatusLabel,
  reviewStatusTooltip,
  setClientErrorNote,
} from "../lib/clientErrorReviewStore.js";
import { formatFilterRangeLabel } from "../lib/dashboardTime.js";
import {
  colorForInteractionSessionId,
  InteractionSessionIdTailCell,
} from "../lib/interactionSessionIdDisplay.js";
import { useReportingTimezone } from "../lib/ReportingTimezoneContext.js";
import type {
  EventNameCount,
  EventsData,
  EventsSortColumn,
  EventsSortDir,
  FilterDevice,
  FilterState,
  InteractionSessionCohort,
  RawEvent,
  SessionJourneyData,
} from "../types.js";

const SESSION_COHORT_OPTIONS: Array<{ value: "" | InteractionSessionCohort; label: string }> = [
  { value: "", label: "Any cohort" },
  { value: "<1m", label: "<1m" },
  { value: "1-5m", label: "1–5m" },
  { value: "5-30m", label: "5–30m" },
  { value: "30m+", label: "30m+" },
];

const JSON_WRAP_STORAGE_KEY = "events-json-word-wrap";

function readJsonWordWrapPreference(): boolean {
  try {
    return localStorage.getItem(JSON_WRAP_STORAGE_KEY) !== "0";
  } catch {
    return true;
  }
}

interface EventsPanelProps {
  data: EventsData | null;
  filters: FilterState;
  pageSize: number;
  onPageSizeChange: (n: number) => void;
  onPrevPage: () => void;
  onNextPage: () => void;
  onGoToPage: (page: number) => void;
  eventName: string;
  onEventNameChange: (value: string) => void;
  propertiesSearch: string;
  onPropertiesSearchChange: (value: string) => void;
  propertiesSearch2: string;
  onPropertiesSearch2Change: (value: string) => void;
  clientErrorBucket: "" | "warning" | "error";
  interactionSessionId: string;
  onInteractionSessionIdChange: (value: string) => void;
  interactionSessionCohort: "" | InteractionSessionCohort;
  onInteractionSessionCohortChange: (value: "" | InteractionSessionCohort) => void;
  showTemperatureEvents: boolean;
  onShowTemperatureEventsChange: (on: boolean) => void;
  showUptimePulse: boolean;
  onShowUptimePulseChange: (on: boolean) => void;
  hideAttractBasics: boolean;
  onHideAttractBasicsChange: (on: boolean) => void;
  dedupePopupTapMissed: boolean;
  onDedupePopupTapMissedChange: (on: boolean) => void;
  /** Inclusive °C lower bound from Health heat-bucket drill-down. */
  tempCelsiusMin?: number;
  /** Exclusive °C upper bound from Health heat-bucket drill-down. */
  tempCelsiusMaxExclusive?: number;
  onClearTempCelsiusRange?: () => void;
  sortBy: EventsSortColumn;
  sortDir: EventsSortDir;
  onSortChange: (column: EventsSortColumn, dir: EventsSortDir) => void;
  from: string;
  to: string;
}

function sortIndicator(active: boolean, dir: EventsSortDir): string {
  if (!active) return "↕";
  return dir === "asc" ? "↑" : "↓";
}

function nextSortDir(column: EventsSortColumn, current: EventsSortColumn, currentDir: EventsSortDir): EventsSortDir {
  if (column === current) return currentDir === "asc" ? "desc" : "asc";
  return column === "client_timestamp" ? "desc" : "asc";
}

const SESSION_ID_PROP_KEYS = new Set(["interaction_session_id", "paid_session_id"]);

/** Prefer high-signal keys first in the Properties column. */
const PROP_PREVIEW_KEY_ORDER = [
  "screen_name",
  "duration_ms",
  "game_name",
  "game_route",
  "game_url",
  "popup_surface",
  "popup",
  "burst_duplicate_count",
  "log_level",
  "message",
  "error",
  "reason",
];

function propSessionId(
  properties: Record<string, unknown> | null,
  key: "interaction_session_id" | "paid_session_id",
): string | null {
  if (!properties) return null;
  const raw = properties[key];
  if (typeof raw !== "string") return null;
  const id = raw.trim();
  return id.length > 0 ? id : null;
}

function formatPropPreviewValue(value: unknown): string {
  if (value === null || value === undefined) return "—";
  if (typeof value === "string") {
    return value.length > 48 ? `${value.slice(0, 48)}…` : value;
  }
  if (typeof value === "number" || typeof value === "boolean") return String(value);
  try {
    const json = JSON.stringify(value);
    return json.length > 36 ? `${json.slice(0, 36)}…` : json;
  } catch {
    return String(value);
  }
}

function propertyPreviewEntries(
  properties: Record<string, unknown> | null,
): Array<[string, unknown]> {
  if (!properties) return [];
  const keys = Object.keys(properties).filter((k) => !SESSION_ID_PROP_KEYS.has(k));
  keys.sort((a, b) => {
    const ai = PROP_PREVIEW_KEY_ORDER.indexOf(a);
    const bi = PROP_PREVIEW_KEY_ORDER.indexOf(b);
    if (ai === -1 && bi === -1) return a.localeCompare(b);
    if (ai === -1) return 1;
    if (bi === -1) return -1;
    return ai - bi;
  });
  return keys.map((k) => [k, properties[k]!]);
}

function propertiesHoverTitle(entries: Array<[string, unknown]>): string {
  if (entries.length === 0) return "(no properties)";
  return entries.map(([k, v]) => `${k}: ${formatPropPreviewValue(v)}`).join("\n");
}

function PropertiesPreviewCell({ properties }: { properties: Record<string, unknown> | null }) {
  const entries = propertyPreviewEntries(properties);
  if (entries.length === 0) {
    return <span className="text-text-secondary/40">—</span>;
  }
  return (
    <div
      className="min-w-0 overflow-hidden text-ellipsis whitespace-nowrap"
      title={propertiesHoverTitle(entries)}
    >
      {entries.map(([key, value], i) => (
        <span key={key}>
          {i > 0 ? <span className="text-text-secondary/50"> · </span> : null}
          <span className="font-semibold text-text-primary">{key}</span>
          <span className="text-text-secondary"> {formatPropPreviewValue(value)}</span>
        </span>
      ))}
    </div>
  );
}

function clientErrorIsWarning(properties: Record<string, unknown> | null): boolean {
  if (!properties) return false;
  const lv = properties.log_level;
  return typeof lv === "string" && lv.toLowerCase() === "warning";
}

function truncateSessionId(id: string): string {
  if (id.length <= 24) return id;
  return `${id.slice(0, 10)}…${id.slice(-8)}`;
}

function formatEventNameOption(n: EventNameCount): string {
  if (n.name !== "client_error") {
    return `${n.name} (${n.count})`;
  }
  const w = n.clientErrorWarningCount ?? 0;
  const err = Math.max(0, n.count - w);
  const parts: string[] = [];
  if (w > 0) parts.push("⚠");
  if (err > 0) parts.push("⛔");
  const icons = parts.join(" ");
  return icons ? `${icons} ${n.name} (${n.count})` : `${n.name} (${n.count})`;
}

/** Sorted page indices for numeric pager buttons (may skip gaps shown as …). */
function pageNumbersForPager(current: number, total: number): number[] {
  if (total <= 1) return [1];
  if (total <= 14) {
    return Array.from({ length: total }, (_, i) => i + 1);
  }
  const want = new Set<number>([1, total, current, current - 1, current + 1, current - 2, current + 2]);
  for (let p = 1; p <= 3; p++) want.add(p);
  for (let p = total - 2; p <= total; p++) want.add(p);
  return [...want].filter((p) => p >= 1 && p <= total).sort((a, b) => a - b);
}

function ClientErrorReviewGlyph({ status }: { status: ClientErrorReviewStatus }) {
  if (status === "unreviewed") {
    return (
      <span
        className="font-bold leading-none text-[color:var(--color-err)]"
        style={{ fontSize: "15px" }}
        aria-hidden
      >
        ?
      </span>
    );
  }
  return (
    <span className="text-[15px] leading-none" aria-hidden>
      {clientErrorReviewIcon(status)}
    </span>
  );
}

function paymentEventMarker(eventName: string): string | null {
  return eventName === "payment_confirmed" || eventName === "payment_succeeded"
    ? "💵"
    : null;
}

function EventNameCell({
  row,
  onReviewStatusChanged,
}: {
  row: RawEvent;
  onReviewStatusChanged: () => void;
}) {
  const isClientError = row.event_name === "client_error";
  const warn = isClientError && clientErrorIsWarning(row.properties);
  const levelIcon = isClientError ? (warn ? "⚠️ " : "⛔ ") : null;
  const paidMarker = paymentEventMarker(row.event_name);

  if (!isClientError) {
    return (
      <span className="inline-flex items-center gap-1 text-text-primary">
        {paidMarker ? (
          <span title={row.event_name} aria-hidden>
            {paidMarker}
          </span>
        ) : null}
        <span>{row.event_name}</span>
      </span>
    );
  }

  const status = getClientErrorReviewStatus(row.id);

  return (
    <span className="inline-flex flex-wrap items-center gap-1 text-text-primary">
      {levelIcon}
      <span>{row.event_name}</span>
      <button
        type="button"
        className="inline-flex cursor-pointer items-center rounded border border-border-subtle bg-panel-2 px-1 py-0.5 text-[13px] leading-none text-text-primary hover:bg-[#223455]"
        title={reviewStatusTooltip(status)}
        aria-label={`Review: ${reviewStatusLabel(status)}. Click to advance status.`}
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          cycleClientErrorReviewStatus(row.id);
          onReviewStatusChanged();
        }}
      >
        <ClientErrorReviewGlyph status={status} />
      </button>
    </span>
  );
}

function EventTableRow({
  row,
  deviceLabel,
  isidAccentColor,
  onReviewStatusChanged,
  onFilterIsid,
  onFilterPsid,
  onOpenNote,
  onOpenJson,
}: {
  row: RawEvent;
  deviceLabel: string;
  isidAccentColor?: string;
  onReviewStatusChanged: () => void;
  onFilterIsid: (id: string) => void;
  onFilterPsid: (id: string) => void;
  onOpenNote: () => void;
  onOpenJson: () => void;
}) {
  const isid = propSessionId(row.properties, "interaction_session_id");
  const psid = propSessionId(row.properties, "paid_session_id");
  const rowStyle = isidAccentColor
    ? { boxShadow: `inset 3px 0 0 ${isidAccentColor}` }
    : undefined;
  return (
    <tr className="border-t border-border-subtle align-top" style={rowStyle}>
      <td className="whitespace-nowrap px-1.5 py-1.5 text-text-secondary">
        <EventTimestampBlock clientTimestamp={row.client_timestamp} compact showUtc={false} />
      </td>
      <td className="truncate px-1.5 py-1.5" title={row.event_name}>
        <EventNameCell row={row} onReviewStatusChanged={onReviewStatusChanged} />
      </td>
      <td className="truncate px-1.5 py-1.5 text-[11px] text-text-secondary" title={row.device_id}>
        {deviceLabel}
      </td>
      <td className="px-0.5 py-1.5 text-center">
        <InteractionSessionIdTailCell
          id={isid}
          label="interaction_session_id"
          accentColor={isidAccentColor}
          onClick={(id) => onFilterIsid(id)}
        />
      </td>
      <td className="px-0.5 py-1.5 text-center">
        <InteractionSessionIdTailCell
          id={psid}
          label="paid_session_id"
          onClick={(id) => onFilterPsid(id)}
        />
      </td>
      <td className="min-w-0 max-w-0 px-1.5 py-1.5 text-[11px] leading-tight">
        <PropertiesPreviewCell properties={row.properties} />
      </td>
      <td className="px-0.5 py-1.5">
        <button
          type="button"
          className="rounded border border-border-subtle bg-panel-2 px-1.5 py-0.5 text-[11px] leading-none text-white hover:bg-[#223455]"
          onClick={onOpenNote}
          title="Add or edit a note (saved in this browser only)"
          aria-label="Add or edit note"
        >
          ✎
        </button>
      </td>
      <td className="px-0.5 py-1.5">
        <button
          type="button"
          className="rounded border border-border-subtle bg-panel-2 px-1.5 py-0.5 text-[11px] leading-none text-white hover:bg-[#223455]"
          onClick={onOpenJson}
          title="Open full properties"
          aria-label="Open full properties"
        >
          ⧉
        </button>
      </td>
    </tr>
  );
}

export function EventsPanel({
  data,
  filters,
  pageSize,
  onPageSizeChange,
  onPrevPage,
  onNextPage,
  onGoToPage,
  eventName,
  onEventNameChange,
  propertiesSearch,
  onPropertiesSearchChange,
  propertiesSearch2,
  onPropertiesSearch2Change,
  clientErrorBucket,
  interactionSessionId,
  onInteractionSessionIdChange,
  interactionSessionCohort,
  onInteractionSessionCohortChange,
  showTemperatureEvents,
  onShowTemperatureEventsChange,
  showUptimePulse,
  onShowUptimePulseChange,
  hideAttractBasics,
  onHideAttractBasicsChange,
  dedupePopupTapMissed,
  onDedupePopupTapMissedChange,
  tempCelsiusMin,
  tempCelsiusMaxExclusive,
  onClearTempCelsiusRange,
  sortBy,
  sortDir,
  onSortChange,
  from,
  to,
}: EventsPanelProps) {
  const reportingTz = useReportingTimezone();
  const [eventSearch, setEventSearch] = useState("");
  const [detailRow, setDetailRow] = useState<RawEvent | null>(null);
  const [detailFocus, setDetailFocus] = useState<"json" | "note">("json");
  const [noteDraft, setNoteDraft] = useState("");
  const [copyState, setCopyState] = useState<"idle" | "copied" | "failed">("idle");
  const [devices, setDevices] = useState<FilterDevice[]>([]);
  const [propsDraft, setPropsDraft] = useState(propertiesSearch);
  const [props2Draft, setProps2Draft] = useState(propertiesSearch2);
  const [isidDraft, setIsidDraft] = useState(interactionSessionId);
  const [, setReviewBump] = useState(0);
  const [jumpDraft, setJumpDraft] = useState("1");
  const [exportLimitStr, setExportLimitStr] = useState("100");
  const [exportBusy, setExportBusy] = useState(false);
  const [exportErr, setExportErr] = useState<string | null>(null);
  const [jsonWordWrap, setJsonWordWrap] = useState(readJsonWordWrapPreference);
  const [journey, setJourney] = useState<SessionJourneyData | null>(null);
  const [journeyLoading, setJourneyLoading] = useState(false);
  const [journeyError, setJourneyError] = useState<string | null>(null);
  /** Keeps journey visible after 💵 drill-down clears the ISID table filter. */
  const [journeyIsid, setJourneyIsid] = useState("");
  const skipJourneyClearRef = useRef(false);
  const noteRef = useRef<HTMLTextAreaElement>(null);
  const onPropsSearchRef = useRef(onPropertiesSearchChange);
  onPropsSearchRef.current = onPropertiesSearchChange;
  const onProps2SearchRef = useRef(onPropertiesSearch2Change);
  onProps2SearchRef.current = onPropertiesSearch2Change;

  useEffect(() => {
    api
      .filters()
      .then((incoming) => {
        setDevices(incoming.devices ?? []);
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    setPropsDraft(propertiesSearch);
  }, [propertiesSearch]);

  useEffect(() => {
    setProps2Draft(propertiesSearch2);
  }, [propertiesSearch2]);

  useEffect(() => {
    setIsidDraft(interactionSessionId);
  }, [interactionSessionId]);

  useEffect(() => {
    const isid = interactionSessionId.trim();
    if (isid) setJourneyIsid(isid);
  }, [interactionSessionId]);

  useEffect(() => {
    if (isidDraft.trim() || interactionSessionId.trim()) return;
    if (skipJourneyClearRef.current) {
      skipJourneyClearRef.current = false;
      return;
    }
    setJourneyIsid("");
  }, [isidDraft, interactionSessionId]);

  useEffect(() => {
    const isid = journeyIsid.trim();
    if (!isid) {
      setJourney(null);
      setJourneyLoading(false);
      setJourneyError(null);
      return;
    }

    let cancelled = false;
    setJourneyLoading(true);
    setJourneyError(null);

    api
      .sessionJourney(filters, isid)
      .then((result) => {
        if (!cancelled) {
          setJourney(result);
          setJourneyLoading(false);
        }
      })
      .catch((err) => {
        if (!cancelled) {
          setJourney(null);
          setJourneyError(err instanceof Error ? err.message : "Failed to load session journey");
          setJourneyLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [
    filters.venueId,
    filters.deviceId,
    filters.gameUrl,
    filters.from,
    filters.to,
    journeyIsid,
  ]);

  useEffect(() => {
    const t = window.setTimeout(() => {
      const next = isidDraft.trim().slice(0, 128);
      if (next !== interactionSessionId) onInteractionSessionIdChange(next);
    }, 400);
    return () => window.clearTimeout(t);
  }, [isidDraft, interactionSessionId, onInteractionSessionIdChange]);

  useEffect(() => {
    const t = window.setTimeout(() => {
      const next = propsDraft.trim().slice(0, 500);
      if (next !== propertiesSearch) onPropsSearchRef.current(next);
    }, 400);
    return () => window.clearTimeout(t);
  }, [propsDraft, propertiesSearch]);

  useEffect(() => {
    const t = window.setTimeout(() => {
      const next = props2Draft.trim().slice(0, 500);
      if (next !== propertiesSearch2) onProps2SearchRef.current(next);
    }, 400);
    return () => window.clearTimeout(t);
  }, [props2Draft, propertiesSearch2]);

  useEffect(() => {
    const onStorage = (e: StorageEvent) => {
      if (
        e.key === CLIENT_ERROR_REVIEW_STORAGE_KEY ||
        e.key === CLIENT_ERROR_NOTES_STORAGE_KEY
      ) {
        setReviewBump((x) => x + 1);
      }
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  useEffect(() => {
    if (detailRow && detailFocus === "note") {
      noteRef.current?.focus();
    }
  }, [detailRow, detailFocus]);

  const deviceLabelById = useMemo(() => {
    const m = new Map<string, string>();
    for (const d of devices) {
      const label = (d.name && d.name.trim()) || d.id;
      m.set(d.id, label);
    }
    return m;
  }, [devices]);

  const applyPsidFilter = useCallback(
    (psid: string) => {
      const needle = `"paid_session_id":"${psid}"`;
      setIsidDraft("");
      onInteractionSessionIdChange("");
      onInteractionSessionCohortChange("");
      onEventNameChange("all");
      setProps2Draft("");
      onPropertiesSearch2Change("");
      setPropsDraft(needle);
      onPropertiesSearchChange(needle);
    },
    [
      onEventNameChange,
      onInteractionSessionCohortChange,
      onInteractionSessionIdChange,
      onPropertiesSearch2Change,
      onPropertiesSearchChange,
    ],
  );

  const jsonPretty = useMemo(() => {
    if (!detailRow) return "";
    return JSON.stringify(detailRow.properties ?? {}, null, 2);
  }, [detailRow]);

  const limit = data?.limit ?? pageSize;
  const total = data?.total ?? 0;
  const offset = data?.offset ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / Math.max(1, limit)));
  const currentPage = Math.floor(offset / Math.max(1, limit)) + 1;
  const rangeStart = total === 0 ? 0 : offset + 1;
  const rangeEnd = offset + (data?.rows.length ?? 0);

  useEffect(() => {
    setJumpDraft(String(currentPage));
  }, [currentPage, total]);

  const filteredEventNames = useMemo(() => {
    if (!data) return [];
    const q = eventSearch.trim().toLowerCase();
    const base = q
      ? data.eventNames.filter((name) => name.name.toLowerCase().includes(q))
      : data.eventNames;
    const sorted = [...base].sort((a, b) => a.name.localeCompare(b.name));

    if (eventName !== "all" && !sorted.some((n) => n.name === eventName)) {
      const active = data.eventNames.find((n) => n.name === eventName);
      const pinned: EventNameCount = {
        name: eventName,
        count: active?.count ?? 0,
        clientErrorWarningCount: active?.clientErrorWarningCount,
      };
      return [pinned, ...sorted.filter((n) => n.name !== eventName)];
    }
    return sorted;
  }, [data, eventName, eventSearch]);

  function openDetail(row: RawEvent, focus: "json" | "note") {
    setDetailRow(row);
    setDetailFocus(focus);
    setNoteDraft(getClientErrorNote(row.id));
    setCopyState("idle");
  }

  function closeDetail() {
    setDetailRow(null);
  }

  function saveNote() {
    if (!detailRow) return;
    setClientErrorNote(detailRow.id, noteDraft);
    setReviewBump((x) => x + 1);
  }

  if (!data) {
    return <p className="py-5 text-[13px] text-text-secondary">No data available</p>;
  }

  function submitJump() {
    const p = Number.parseInt(jumpDraft, 10);
    if (!Number.isFinite(p)) return;
    onGoToPage(p);
  }

  async function handleExportDownload() {
    const raw = Number.parseInt(exportLimitStr.trim(), 10);
    const exportLimit = Number.isFinite(raw) ? raw : 100;
    setExportBusy(true);
    setExportErr(null);
    try {
      await api.downloadRawEventsExportJson(filters, {
        exportLimit,
        eventName,
        propertiesContains: propertiesSearch,
        propertiesContains2: propertiesSearch2,
        clientErrorBucket:
          eventName === "client_error" && (clientErrorBucket === "warning" || clientErrorBucket === "error")
            ? clientErrorBucket
            : undefined,
        interactionSessionId: interactionSessionId || undefined,
        interactionSessionCohort: interactionSessionCohort || undefined,
        showTemperatureEvents,
        showUptimePulse,
        hideAttractBasics,
        dedupePopupTapMissed,
        tempCelsiusMin,
        tempCelsiusMaxExclusive,
        sortBy,
        sortDir,
      });
    } catch (e) {
      setExportErr(e instanceof Error ? e.message : "Export failed");
    } finally {
      setExportBusy(false);
    }
  }

  const pageNums = pageNumbersForPager(currentPage, totalPages);

  const selectedJourneySegmentKey = useMemo(() => {
    if (!journey) return null;
    for (const seg of journey.segments) {
      const f = segmentRawEventsFilters(seg);
      if (
        eventName === f.eventName &&
        propertiesSearch === f.propertiesContains &&
        propertiesSearch2 === f.propertiesContains2
      ) {
        return segmentKey(seg);
      }
    }
    return null;
  }, [journey, eventName, propertiesSearch, propertiesSearch2]);

  return (
    <div className="space-y-3">
      {journeyIsid.trim() ? (
        journeyLoading ? (
          <p className="text-[12px] text-text-secondary">Loading session journey…</p>
        ) : journeyError ? (
          <p className="text-[12px] text-err">Session journey: {journeyError}</p>
        ) : journey ? (
          <SessionJourneyBar
            journey={journey}
            selectedSegmentKey={selectedJourneySegmentKey}
            onSegmentClick={(_seg, f) => {
              onEventNameChange(f.eventName);
              onPropertiesSearchChange(f.propertiesContains);
              onPropertiesSearch2Change(f.propertiesContains2);
            }}
            onPaymentConfirmedClick={(paidSessionId) => {
              skipJourneyClearRef.current = true;
              setIsidDraft("");
              onInteractionSessionIdChange("");
              onEventNameChange("payment_confirmed");
              onPropertiesSearch2Change("");
              onPropertiesSearchChange(
                paidSessionId ? `"paid_session_id":"${paidSessionId}"` : "",
              );
            }}
          />
        ) : null
      ) : null}

      <div className="flex flex-wrap items-end gap-3">
        <div className="flex flex-wrap items-end gap-3">
          <div className="flex flex-wrap gap-2">
            <div className="flex flex-col gap-1">
              <label className="text-[11px] uppercase tracking-wide text-text-secondary">
                Search Events
              </label>
              <input
                className="min-w-[200px] rounded-lg border border-border-subtle bg-panel-2 px-2 py-2 text-[13px] text-text-primary"
                value={eventSearch}
                onChange={(e) => setEventSearch(e.target.value)}
                placeholder="Type event name..."
              />
            </div>
            <div className="flex flex-col gap-1">
              <label className="text-[11px] uppercase tracking-wide text-text-secondary">
                Event Name
              </label>
              <select
                className="min-w-[240px] rounded-lg border border-border-subtle bg-panel-2 px-2 py-2 text-[13px] text-text-primary"
                value={eventName}
                onChange={(e) => onEventNameChange(e.target.value)}
              >
                <option value="all">all</option>
                {filteredEventNames.map((name) => (
                  <option key={name.name} value={name.name}>
                    {formatEventNameOption(name)}
                  </option>
                ))}
              </select>
            </div>
            <div className="flex min-w-[200px] max-w-[320px] flex-col gap-1">
              <label className="text-[11px] uppercase tracking-wide text-text-secondary">
                Properties contains
              </label>
              <input
                className="w-full rounded-lg border border-border-subtle bg-panel-2 px-2 py-2 text-[13px] text-text-primary"
                value={propsDraft}
                onChange={(e) => setPropsDraft(e.target.value)}
                placeholder="Substring in JSON (e.g. error message)…"
              />
            </div>
            <div className="flex min-w-[200px] max-w-[320px] flex-col gap-1">
              <label className="text-[11px] uppercase tracking-wide text-text-secondary">
                Properties also (AND)
              </label>
              <input
                className="w-full rounded-lg border border-border-subtle bg-panel-2 px-2 py-2 text-[13px] text-text-primary"
                value={props2Draft}
                onChange={(e) => setProps2Draft(e.target.value)}
                placeholder="Second substring (optional)…"
              />
            </div>
            <div className="flex min-w-[200px] max-w-[280px] flex-col gap-1">
              <label className="text-[11px] uppercase tracking-wide text-text-secondary">
                Interaction session id
              </label>
              <input
                className="w-full rounded-lg border border-border-subtle bg-panel-2 px-2 py-2 font-mono text-[12px] text-text-primary"
                value={isidDraft}
                onChange={(e) => setIsidDraft(e.target.value)}
                placeholder="Exact id (UUID)…"
              />
            </div>
            <div className="flex min-w-[140px] flex-col gap-1">
              <label className="text-[11px] uppercase tracking-wide text-text-secondary">
                Session cohort
              </label>
              <select
                className="rounded-lg border border-border-subtle bg-panel-2 px-2 py-2 text-[13px] text-text-primary"
                value={interactionSessionCohort}
                onChange={(e) =>
                  onInteractionSessionCohortChange(
                    e.target.value as "" | InteractionSessionCohort,
                  )
                }
              >
                {SESSION_COHORT_OPTIONS.map((o) => (
                  <option key={o.label} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
            </div>
          </div>
          <div className="flex min-w-0 flex-wrap items-center gap-x-3 gap-y-0 text-[11px] leading-tight text-text-secondary">
            <span>
              Showing {data.rows.length} of {total} matching events ({rangeStart}–{rangeEnd} this page)
              {" · "}
              Range (local): {formatFilterRangeLabel(from, to, reportingTz)}
              {propertiesSearch ? ` · Props: “${propertiesSearch.slice(0, 40)}${propertiesSearch.length > 40 ? "…" : ""}”` : ""}
              {propertiesSearch2
                ? ` · AND “${propertiesSearch2.slice(0, 40)}${propertiesSearch2.length > 40 ? "…" : ""}”`
                : ""}
              {interactionSessionId
                ? ` · Session id: ${truncateSessionId(interactionSessionId)}`
                : ""}
              {interactionSessionCohort ? ` · Cohort: ${interactionSessionCohort}` : ""}
              {tempCelsiusMin != null || tempCelsiusMaxExclusive != null
                ? ` · Heat °C: ${tempCelsiusMin != null ? `≥${tempCelsiusMin}` : ""}${
                    tempCelsiusMin != null && tempCelsiusMaxExclusive != null ? " and " : ""
                  }${tempCelsiusMaxExclusive != null ? `<${tempCelsiusMaxExclusive}` : ""}`
                : ""}
            </span>
            {(tempCelsiusMin != null || tempCelsiusMaxExclusive != null) && onClearTempCelsiusRange ? (
              <button
                type="button"
                className="rounded border border-border-subtle bg-panel-2 px-1.5 py-0.5 text-[11px] text-text-primary hover:bg-[#223455]"
                onClick={onClearTempCelsiusRange}
                title="Clear Health heat-bucket temperature range filter"
              >
                Clear °C range
              </button>
            ) : null}
            <span className="inline-flex flex-wrap items-center gap-1 border-border-subtle border-l pl-3">
              <label className="inline-flex cursor-pointer items-center gap-1 text-text-secondary">
                <input
                  type="checkbox"
                  className="accent-accent-2"
                  checked={showTemperatureEvents}
                  onChange={(e) => onShowTemperatureEventsChange(e.target.checked)}
                />
                Temperature events
              </label>
              <label className="inline-flex cursor-pointer items-center gap-1 text-text-secondary">
                <input
                  type="checkbox"
                  className="accent-accent-2"
                  checked={showUptimePulse}
                  onChange={(e) => onShowUptimePulseChange(e.target.checked)}
                />
                Uptime pulse
              </label>
              <label
                className="inline-flex cursor-pointer items-center gap-1 text-text-secondary"
                title="Show attract_screen_exited and attract_screen_viewed"
              >
                <input
                  type="checkbox"
                  className="accent-accent-2"
                  checked={!hideAttractBasics}
                  onChange={(e) => onHideAttractBasicsChange(!e.target.checked)}
                />
                Attract basics
              </label>
              <label
                className="inline-flex cursor-pointer items-center gap-1 text-text-secondary"
                title="Collapse consecutive popup_tap_missed rows (same session/game, ≤30s apart) to one row with burst_duplicate_count"
              >
                <input
                  type="checkbox"
                  className="accent-accent-2"
                  checked={dedupePopupTapMissed}
                  onChange={(e) => onDedupePopupTapMissedChange(e.target.checked)}
                />
                Collapse missed taps
              </label>
            </span>
            <span className="inline-flex flex-wrap items-center gap-1 border-border-subtle border-l pl-3">
              <span className="text-text-secondary">Export JSON</span>
              <input
                type="number"
                min={1}
                max={1000}
                className="w-11 rounded border border-border-subtle bg-panel-2 px-1 py-0.5 text-[11px] text-text-primary tabular-nums"
                value={exportLimitStr}
                onChange={(e) => setExportLimitStr(e.target.value)}
                title="Rows to download (1–1000), newest first"
                aria-label="Export row count"
              />
              <button
                type="button"
                className="rounded border border-border-subtle bg-panel-2 px-1.5 py-0.5 text-[11px] text-text-primary hover:bg-[#223455] disabled:opacity-40"
                disabled={exportBusy}
                onClick={() => void handleExportDownload()}
              >
                {exportBusy ? "…" : "Download"}
              </button>
            </span>
            {exportErr ? <span className="text-[11px] text-err">{exportErr}</span> : null}
          </div>
        </div>
      </div>

      <div className="overflow-auto rounded-xl border border-border">
        <table className="table-fixed min-w-[720px] w-full border-collapse text-left text-[12px]">
          <colgroup>
            <col className="w-[11.5rem]" />
            <col className="w-[9.5rem]" />
            <col className="w-[5.5rem]" />
            <col className="w-[2.75rem]" />
            <col className="w-[2.75rem]" />
            <col />
            <col className="w-[2.25rem]" />
            <col className="w-[2.25rem]" />
          </colgroup>
          <thead className="bg-panel-3 text-text-secondary">
            <tr>
              {(
                [
                  ["client_timestamp", "Time"],
                  ["event_name", "Event"],
                  ["device_id", "Device"],
                ] as const
              ).map(([column, label]) => (
                <th key={column} className="px-1.5 py-2 font-semibold">
                  <button
                    type="button"
                    className="inline-flex cursor-pointer items-center gap-0.5 text-left hover:text-text-primary"
                    title={`Sort by ${label}`}
                    onClick={() =>
                      onSortChange(column, nextSortDir(column, sortBy, sortDir))
                    }
                  >
                    <span>{label}</span>
                    <span className="text-[10px] text-text-secondary" aria-hidden>
                      {sortIndicator(sortBy === column, sortDir)}
                    </span>
                  </button>
                </th>
              ))}
              <th
                className="px-0.5 py-2 text-center font-semibold"
                title="interaction_session_id — visitor stint id from games shell. Shows last 4 characters; hover a cell for the full id. Click a cell to filter to that session."
              >
                ISID
              </th>
              <th
                className="px-0.5 py-2 text-center font-semibold"
                title='paid_session_id — paid play stint linked on the event. Shows last 4 characters; hover a cell for the full id. Click a cell to filter Raw Events to Properties containing "paid_session_id":"…" (clears other event filters).'
              >
                PSID
              </th>
              <th className="min-w-0 px-1.5 py-2 font-semibold">
                <button
                  type="button"
                  className="inline-flex cursor-pointer items-center gap-0.5 text-left hover:text-text-primary"
                  title="Sort by Properties"
                  onClick={() =>
                    onSortChange("properties", nextSortDir("properties", sortBy, sortDir))
                  }
                >
                  <span>Properties</span>
                  <span className="text-[10px] text-text-secondary" aria-hidden>
                    {sortIndicator(sortBy === "properties", sortDir)}
                  </span>
                </button>
              </th>
              <th className="px-0.5 py-2 font-semibold">Note</th>
              <th className="px-0.5 py-2 font-semibold">View</th>
            </tr>
          </thead>
          <tbody>
            {data.rows.map((row) => {
              const isid = propSessionId(row.properties, "interaction_session_id");
              return (
                <EventTableRow
                  key={row.id}
                  row={row}
                  deviceLabel={deviceLabelById.get(row.device_id) ?? row.device_id}
                  isidAccentColor={isid ? colorForInteractionSessionId(isid) : undefined}
                  onReviewStatusChanged={() => setReviewBump((x) => x + 1)}
                  onFilterIsid={(id) => {
                    setIsidDraft(id);
                    onInteractionSessionIdChange(id);
                  }}
                  onFilterPsid={applyPsidFilter}
                  onOpenNote={() => openDetail(row, "note")}
                  onOpenJson={() => openDetail(row, "json")}
                />
              );
            })}
          </tbody>
        </table>

        <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-1 border-t border-border-subtle px-2 py-1.5 text-[11px] text-text-secondary">
          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              className="rounded border border-border-subtle bg-panel-2 px-2 py-1 text-text-primary disabled:cursor-not-allowed disabled:opacity-40"
              onClick={onPrevPage}
              disabled={offset === 0}
            >
              Previous
            </button>
            <button
              type="button"
              className="rounded border border-border-subtle bg-panel-2 px-2 py-1 text-text-primary disabled:cursor-not-allowed disabled:opacity-40"
              onClick={onNextPage}
              disabled={!data.hasMore}
            >
              Next
            </button>
            <span className="tabular-nums">
              Page {currentPage} of {totalPages}
            </span>
            <label className="inline-flex items-center gap-1">
              <span className="text-text-secondary">Rows/page</span>
              <select
                className="rounded border border-border-subtle bg-panel-2 px-1 py-0.5 text-[11px] text-text-primary"
                value={pageSize}
                onChange={(e) => onPageSizeChange(Number.parseInt(e.target.value, 10))}
              >
                {[25, 50, 100, 200].map((n) => (
                  <option key={n} value={n}>
                    {n}
                  </option>
                ))}
              </select>
            </label>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <div className="flex max-w-[min(100vw-24px,520px)] flex-wrap items-center gap-1 overflow-x-auto py-0.5">
              {pageNums.map((p, i) => (
                <Fragment key={p}>
                  {i > 0 && pageNums[i - 1] !== undefined && p > pageNums[i - 1]! + 1 ? (
                    <span className="px-0.5 text-text-secondary" aria-hidden>
                      …
                    </span>
                  ) : null}
                  <button
                    type="button"
                    className={`min-w-[1.5rem] rounded border px-1.5 py-0.5 tabular-nums ${
                      p === currentPage
                        ? "border-accent-2 bg-panel-3 font-semibold text-text-primary"
                        : "border-border-subtle bg-panel-2 text-text-primary hover:bg-[#223455]"
                    }`}
                    onClick={() => onGoToPage(p)}
                  >
                    {p}
                  </button>
                </Fragment>
              ))}
            </div>
            <span className="inline-flex items-center gap-1 border-border-subtle border-l pl-2">
              <span>Go to</span>
              <input
                type="number"
                min={1}
                max={totalPages}
                className="w-11 rounded border border-border-subtle bg-panel-2 px-1 py-0.5 text-[11px] text-text-primary tabular-nums"
                value={jumpDraft}
                onChange={(e) => setJumpDraft(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") submitJump();
                }}
                aria-label="Page number"
              />
              <button
                type="button"
                className="rounded border border-border-subtle bg-panel-2 px-1.5 py-0.5 text-[11px] text-text-primary hover:bg-[#223455]"
                onClick={submitJump}
              >
                Go
              </button>
            </span>
          </div>
        </div>
      </div>

      {detailRow !== null && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
          <div className="flex max-h-[90vh] w-full max-w-3xl flex-col rounded-xl border border-border bg-panel p-3 shadow-xl">
            <div className="mb-2 shrink-0 flex items-start justify-between gap-2">
              <div className="min-w-0 text-sm font-semibold text-text-primary">
                {detailRow.event_name === "client_error" ? (
                  <span className="inline-flex flex-wrap items-center gap-1">
                    <button
                      type="button"
                      className="inline-flex cursor-pointer items-center gap-1 rounded border border-border-subtle bg-panel-2 px-1.5 py-0.5 text-sm text-text-primary hover:bg-[#223455]"
                      title={reviewStatusTooltip(getClientErrorReviewStatus(detailRow.id))}
                      aria-label={`Review: ${reviewStatusLabel(getClientErrorReviewStatus(detailRow.id))}. Click to advance status.`}
                      onClick={() => {
                        cycleClientErrorReviewStatus(detailRow.id);
                        setReviewBump((x) => x + 1);
                      }}
                    >
                      <ClientErrorReviewGlyph status={getClientErrorReviewStatus(detailRow.id)} />
                      <span>{reviewStatusLabel(getClientErrorReviewStatus(detailRow.id))}</span>
                    </button>
                    <span className="text-text-secondary">·</span>
                    <span>{detailRow.event_name}</span>
                    <span className="text-text-secondary">·</span>
                    <EventTimestampBlock
                      clientTimestamp={detailRow.client_timestamp}
                      receivedAt={detailRow.received_at}
                      compact
                    />
                  </span>
                ) : (
                  <span className="inline-flex flex-wrap items-center gap-1">
                    {paymentEventMarker(detailRow.event_name) ? (
                      <span title={detailRow.event_name} aria-hidden>
                        {paymentEventMarker(detailRow.event_name)}
                      </span>
                    ) : null}
                    {detailRow.event_name}
                    <EventTimestampBlock
                      clientTimestamp={detailRow.client_timestamp}
                      receivedAt={detailRow.received_at}
                      compact
                    />
                  </span>
                )}
              </div>
              <div className="flex shrink-0 gap-2">
                <button
                  type="button"
                  className="rounded border border-border-subtle bg-panel-2 px-3 py-1 text-xs text-text-primary hover:bg-[#223455]"
                  onClick={async () => {
                    try {
                      await navigator.clipboard.writeText(jsonPretty);
                      setCopyState("copied");
                    } catch {
                      setCopyState("failed");
                    }
                  }}
                >
                  {copyState === "copied" ? "Copied" : copyState === "failed" ? "Copy failed" : "Copy JSON"}
                </button>
                <button
                  type="button"
                  className="rounded border border-border-subtle bg-panel-2 px-3 py-1 text-xs text-text-primary hover:bg-[#223455]"
                  onClick={closeDetail}
                >
                  Close
                </button>
              </div>
            </div>

            <div className="min-h-0 flex-1 space-y-3 overflow-auto">
              <div>
                <div className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-text-secondary">
                  Note
                </div>
                <textarea
                  ref={noteRef}
                  className="h-24 w-full resize-y rounded border border-border-subtle bg-panel-2 px-2 py-2 font-mono text-[12px] text-text-primary"
                  value={noteDraft}
                  onChange={(e) => setNoteDraft(e.target.value)}
                  placeholder="Private note (this browser only)…"
                  spellCheck
                />
                <button
                  type="button"
                  className="mt-1 rounded border border-border-subtle bg-panel-2 px-2 py-1 text-[11px] text-text-primary hover:bg-[#223455]"
                  onClick={saveNote}
                >
                  Save note
                </button>
              </div>

              <div>
                <div className="mb-1 flex items-center justify-between gap-2">
                  <span className="text-[11px] font-semibold uppercase tracking-wide text-text-secondary">
                    Event JSON
                  </span>
                  <button
                    type="button"
                    className="rounded border border-border-subtle bg-panel-2 px-2 py-0.5 text-[11px] text-text-primary hover:bg-[#223455]"
                    onClick={() => {
                      setJsonWordWrap((on) => {
                        const next = !on;
                        try {
                          localStorage.setItem(JSON_WRAP_STORAGE_KEY, next ? "1" : "0");
                        } catch {
                          /* ignore */
                        }
                        return next;
                      });
                    }}
                    aria-pressed={jsonWordWrap}
                    title={jsonWordWrap ? "Disable word wrap" : "Enable word wrap"}
                  >
                    Word wrap: {jsonWordWrap ? "On" : "Off"}
                  </button>
                </div>
                <pre
                  className={`max-h-[45vh] overflow-auto rounded bg-panel-3 p-3 font-mono text-[12px] text-text-primary ${
                    jsonWordWrap ? "whitespace-pre-wrap break-words" : "whitespace-pre"
                  }`}
                >
                  {jsonPretty}
                </pre>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

